import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowRight, Clock, Calendar, CheckCircle2, Zap, TrendingUp, AlertTriangle, ArrowLeft, Coffee } from 'lucide-react';

export default function StopWastingTimeBlog() {
  const navigate = useNavigate();
  const [visibleSections, setVisibleSections] = useState(new Set());
  const observerRef = useRef(null);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections(prev => new Set([...prev, entry.target.dataset.animate]));
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -100px 0px' }
    );

    const sections = document.querySelectorAll('[data-animate]');
    sections.forEach((section) => observerRef.current.observe(section));

    return () => observerRef.current.disconnect();
  }, []);

  return (
    <div className="bg-white text-zinc-900">
      {/* Back Button */}
      <div className="bg-zinc-50 border-b border-zinc-200">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-4">
          <Link to="/resources/blogs" className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-semibold transition-colors text-sm">
            <ArrowLeft size={16} />
            Back to Blogs
          </Link>
        </div>
      </div>

      {/* Hero Section */}
      <article className="max-w-4xl mx-auto px-6 lg:px-8 py-12">
        <div data-animate="hero" className={`transition-all duration-1000 ease-out ${
          visibleSections.has('hero') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <div className="inline-block px-3 py-1.5 bg-blue-600 text-white text-xs font-semibold tracking-wider mb-4 rounded">
            MARKETING TIPS
          </div>

          <h1 className="text-3xl lg:text-4xl font-bold mb-4 leading-tight text-zinc-900">
            Stop Wasting Time on Manual Marketing: Here's What Actually Works
          </h1>

          <div className="flex flex-wrap items-center gap-6 text-sm text-zinc-500 mb-8 pb-8 border-b border-zinc-200">
            <div className="flex items-center gap-2">
              <Calendar size={16} className="text-zinc-400" />
              <span>29 December 2024</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock size={16} className="text-zinc-400" />
              <span>5 min read</span>
            </div>
            <div>
              <span className="font-medium">By Digital Trent Team</span>
            </div>
          </div>
        </div>

        {/* Introduction */}
        <section data-animate="intro" className={`mb-12 transition-all duration-1000 ease-out ${
          visibleSections.has('intro') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <div className="prose prose-lg max-w-none text-zinc-700">
            <p className="text-lg leading-relaxed mb-6">
              Let me guess: you're spending 20+ hours every week on marketing tasks that <strong className="text-zinc-900">feel productive but don't move the needle</strong>. Creating social posts one by one. Manually scheduling emails. Copying and pasting the same follow-ups to leads. Sound familiar?
            </p>
            <p className="leading-relaxed mb-6">
              Here's the uncomfortable truth: whilst you're drowning in these manual tasks, your competitors have automated them and are using that extra 20 hours to actually grow their businesses.
            </p>
            <p className="leading-relaxed">
              This post isn't about working harder—it's about working smarter. Let me show you exactly what successful small businesses are doing differently.
            </p>
          </div>
        </section>

        {/* The Time Trap */}
        <section data-animate="problem" className={`mb-12 transition-all duration-1000 ease-out ${
          visibleSections.has('problem') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-4 text-zinc-900">
            <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
              <AlertTriangle size={22} className="text-white" />
            </div>
            The Manual Marketing Time Trap
          </h2>

          <p className="text-zinc-700 mb-6 leading-relaxed text-lg">
            You probably recognise these time-wasting patterns:
          </p>

          <div className="space-y-4 mb-8">
            {[
              { task: 'Creating this week\'s social posts', time: '3-4 hours', reality: 'Could be done in 10 minutes with AI' },
              { task: 'Scheduling posts across platforms', time: '2-3 hours', reality: 'Should be 100% automated' },
              { task: 'Writing individual follow-up emails', time: '4-5 hours', reality: 'AI personalises at scale instantly' },
              { task: 'Manually tracking campaign performance', time: '2-3 hours', reality: 'AI monitors 24/7 automatically' }
            ].map((item, i) => (
              <div key={i} className="bg-red-50 border border-red-200 p-6 rounded-lg">
                <div className="flex items-start justify-between mb-2">
                  <h4 className="font-bold text-zinc-900">{item.task}</h4>
                  <span className="text-red-600 font-semibold text-sm">{item.time}/week</span>
                </div>
                <p className="text-zinc-600 text-sm">{item.reality}</p>
              </div>
            ))}
          </div>

          <div className="bg-blue-50 border-l-4 border-blue-600 p-8 rounded-r-lg">
            <p className="text-zinc-900 font-bold text-lg mb-2">Total Time Wasted: 15-20+ Hours Per Week</p>
            <p className="text-zinc-700 leading-relaxed">That's 80 hours per month you could be spending on strategy, product development, or actually talking to customers.</p>
          </div>
        </section>

        {/* What Actually Works */}
        <section data-animate="solution" className={`mb-12 transition-all duration-1000 ease-out ${
          visibleSections.has('solution') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-4 text-zinc-900">
            <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
              <Zap size={22} className="text-white" />
            </div>
            Here's What Smart Businesses Do Instead
          </h2>

          <p className="text-lg text-zinc-700 leading-relaxed mb-10">
            Successful small businesses have cracked the code: they automate the repetitive stuff and focus human time on high-value activities. Here's their playbook:
          </p>

          <div className="space-y-6">
            {[
              {
                num: '1',
                title: 'Automate Content Creation',
                desc: 'Use AI to generate months of social posts, emails, and blog drafts in one session. You review and approve—not write from scratch.',
                benefit: 'Saves 6-8 hours/week',
                color: 'blue'
              },
              {
                num: '2',
                title: 'Set Up Smart Scheduling',
                desc: 'Configure your posting preferences once. AI handles optimal timing, platform distribution, and queue management forever.',
                benefit: 'Saves 3-4 hours/week',
                color: 'green'
              },
              {
                num: '3',
                title: 'Enable Automatic Lead Nurturing',
                desc: 'Create trigger-based sequences that send personalised follow-ups based on behaviour—no manual tracking needed.',
                benefit: 'Saves 4-5 hours/week',
                color: 'purple'
              },
              {
                num: '4',
                title: 'Implement Real-Time Analytics',
                desc: 'Let AI monitor performance 24/7 and alert you to what needs attention. No more manual report building.',
                benefit: 'Saves 2-3 hours/week',
                color: 'orange'
              }
            ].map((item, idx) => (
              <div key={idx} className="relative bg-white border border-zinc-200 p-6 rounded-xl hover:border-blue-300 hover:shadow-lg hover:shadow-blue-100/50 transition-all duration-300">
                {/* Faded Micro Grid */}
                <div className="absolute top-0 left-0 w-24 h-24 pointer-events-none opacity-[0.06] overflow-hidden rounded-tl-xl">
                  <div
                    style={{
                      backgroundImage: `linear-gradient(${item.color === 'blue' ? '#3b82f6' : item.color === 'green' ? '#10b981' : item.color === 'purple' ? '#8b5cf6' : '#f97316'} 0.5px, transparent 0.5px),
                                        linear-gradient(90deg, ${item.color === 'blue' ? '#3b82f6' : item.color === 'green' ? '#10b981' : item.color === 'purple' ? '#8b5cf6' : '#f97316'} 0.5px, transparent 0.5px)`,
                      backgroundSize: '8px 8px',
                      width: '100%',
                      height: '100%'
                    }}
                  />
                </div>

                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-blue-600 text-white rounded-lg flex items-center justify-center font-bold text-lg shadow-sm">
                    {item.num}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-zinc-900 mb-2">{item.title}</h3>
                    <p className="text-zinc-700 mb-3 leading-relaxed">{item.desc}</p>
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-green-50 border border-green-200 rounded-lg">
                      <CheckCircle2 size={16} className="text-green-600" />
                      <span className="text-sm font-semibold text-green-700">{item.benefit}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* The Reality Check */}
        <section data-animate="reality" className={`mb-12 transition-all duration-1000 ease-out ${
          visibleSections.has('reality') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-4 text-zinc-900">
            <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
              <TrendingUp size={22} className="text-white" />
            </div>
            The Real Impact on Your Business
          </h2>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-red-50 border-2 border-red-200 p-6 rounded-xl">
              <h4 className="font-bold text-lg mb-4 text-zinc-900">Without Automation:</h4>
              <ul className="space-y-2 text-zinc-700">
                <li className="flex items-start gap-2">
                  <span className="text-red-500 mt-1">•</span>
                  <span>20+ hours/week on repetitive tasks</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 mt-1">•</span>
                  <span>Inconsistent posting and follow-up</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 mt-1">•</span>
                  <span>Missed opportunities from slow response</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 mt-1">•</span>
                  <span>Generic messaging that doesn't convert</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 mt-1">•</span>
                  <span>Burning out trying to "do it all"</span>
                </li>
              </ul>
            </div>

            <div className="bg-green-50 border-2 border-green-200 p-6 rounded-xl">
              <h4 className="font-bold text-lg mb-4 text-zinc-900">With Smart Automation:</h4>
              <ul className="space-y-2 text-zinc-700">
                <li className="flex items-start gap-2">
                  <CheckCircle2 size={18} className="text-green-600 mt-0.5 flex-shrink-0" />
                  <span>1-2 hours/week maintaining marketing</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 size={18} className="text-green-600 mt-0.5 flex-shrink-0" />
                  <span>Consistent, on-brand content 24/7</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 size={18} className="text-green-600 mt-0.5 flex-shrink-0" />
                  <span>Instant, personalised lead follow-up</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 size={18} className="text-green-600 mt-0.5 flex-shrink-0" />
                  <span>Data-driven optimisation running 24/7</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 size={18} className="text-green-600 mt-0.5 flex-shrink-0" />
                  <span>Time to focus on strategy and growth</span>
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* Take Action */}
        <section data-animate="action" className={`mb-12 transition-all duration-1000 ease-out ${
          visibleSections.has('action') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <h2 className="text-2xl md:text-3xl font-bold mb-6 text-zinc-900">Your Next Step</h2>

          <div className="text-zinc-700 leading-relaxed space-y-6">
            <p className="text-lg">
              You have two choices: keep doing manual marketing the hard way, or embrace automation and get 20 hours of your life back every week.
            </p>

            <p className="text-lg">
              The businesses that thrive in 2025 aren't working harder—they're working smarter. They've realised that <strong className="text-zinc-900">automation isn't lazy, it's strategic</strong>. It's the only way to compete without burning out or hiring a huge team.
            </p>

            <p className="text-lg">
              Stop wasting time on tasks that software can handle better. Start focusing on the things only you can do—like building relationships, crafting strategy, and growing your business.
            </p>
          </div>
        </section>

        {/* CTA Section */}
        <section data-animate="cta" className={`transition-all duration-1000 ease-out ${
          visibleSections.has('cta') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <div className="bg-gradient-to-br from-blue-600 to-blue-700 p-8 lg:p-10 rounded-2xl text-center shadow-xl">
            <h3 className="text-2xl md:text-3xl font-bold mb-4 text-white">Ready to Get 20 Hours Back?</h3>
            <p className="text-lg text-blue-50 mb-10 max-w-2xl mx-auto leading-relaxed">
              See how Digital Trent automates your marketing so you can focus on what actually matters.
            </p>
            <div className="flex flex-col sm:flex-row gap-5 justify-center">
              <button
                onClick={() => navigate('/auth/signup')}
                className="group bg-white text-blue-600 hover:bg-blue-50 px-10 py-4 rounded-xl font-bold text-lg transition-all duration-300 inline-flex items-center justify-center gap-3 shadow-lg hover:shadow-xl"
              >
                Start Free Trial
                <ArrowRight size={22} className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
              <button
                onClick={() => navigate('/company/contact')}
                className="border-2 border-white text-white hover:bg-white hover:text-blue-600 px-10 py-4 rounded-xl font-bold text-lg transition-all duration-300"
              >
                Book a Demo
              </button>
            </div>
            <p className="text-sm text-blue-50 mt-6">No credit card required • 14-day free trial • Cancel any time</p>
          </div>
        </section>
      </article>
    </div>
  );
}
